/* ===== Custom Search Module ===== */
/* Layers on top of theme's SearchBar (D-01). Does not modify theme.js. */
/* If this module fails to load, theme's native search still works. */

const CustomSearch = {
  // === State ===
  _input: null,
  _form: null,
  _dropdown: null,
  _liveRegion: null,
  _abortController: null,
  _debounceTimer: null,
  _cache: new Map(),      // In-memory cache (D-07)
  _isOpen: false,
  _activeIndex: -1,
  _results: [],           // Array of focusable result elements
  _currentQuery: '',
  _currentConcernName: null,

  // === Constants ===
  API_RESULT_LIMIT: 6,
  CACHE_MAX_ENTRIES: 100,                 // LRU eviction cap for in-memory cache (WR-05)
  DEBOUNCE_MS: 250,
  MIN_QUERY_LENGTH: 2,
  PREDICTIVE_CACHE_TTL: 5 * 60 * 1000,   // 5 min (PERF-05)
  SECTION_CACHE_TTL: 10 * 60 * 1000,     // 10 min (AUTO-09)
  SYNONYM_MIN_PRODUCT_COUNT: 3,           // Min predictive hits before synonym follow-up fires (SYNN-01)

  // === Initialization ===

  init() {
    this._input = document.querySelector('.search-bar__input');
    if (!this._input) return;
    this._form = this._input.closest('.search-bar');
    if (!this._form) return;

    // ARIA combobox pattern (AUTO-12)
    this._input.setAttribute('role', 'combobox');
    this._input.setAttribute('aria-expanded', 'false');
    this._input.setAttribute('aria-controls', 'custom-search-results');
    this._input.setAttribute('aria-autocomplete', 'list');
    this._input.setAttribute('aria-activedescendant', '');

    this._createDropdown();
    this._attachListeners();

    // D-02: trigger CSS that hides theme's search results and Searchanise
    this._form.classList.add('custom-search-active');

    // Clean up Searchanise class if it touched the input before being blocked
    this._input.classList.remove('snize-input-style');

    // Prevent theme's mobile full-screen search takeover.
    // The theme's SearchBar uses Delegate on .header__search-bar-wrapper to
    // listen for focusin on [name="q"]. Capture phase fires before the event
    // bubbles to the delegate, and stopImmediatePropagation prevents it from
    // reaching the wrapper. Result: _onInputFocus never runs, is-fixed never
    // gets added, and the header stays in place on mobile.
    this._input.addEventListener('focusin', function(e) {
      e.stopImmediatePropagation();
    }, true);
  },

  _createDropdown() {
    // Dropdown container with ARIA listbox role
    const dropdown = document.createElement('div');
    dropdown.className = 'custom-search__dropdown';
    dropdown.setAttribute('role', 'listbox');
    dropdown.id = 'custom-search-results';
    dropdown.setAttribute('aria-label', 'Search results');
    dropdown.setAttribute('data-testid', 'search-dropdown');
    this._dropdown = dropdown;

    // Live region for screen reader announcements (AUTO-13)
    const liveRegion = document.createElement('div');
    liveRegion.className = 'custom-search__sr-only';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    this._liveRegion = liveRegion;

    // Insert dropdown after .search-bar__inner, or append to form
    const inner = this._form.querySelector('.search-bar__inner');
    if (inner) {
      inner.parentNode.insertBefore(dropdown, inner.nextSibling);
    } else {
      this._form.appendChild(dropdown);
    }
    this._form.appendChild(liveRegion);
  },

  _attachListeners() {
    this._input.addEventListener(
      'input',
      this._debounce(this._onInput.bind(this), this.DEBOUNCE_MS)
    );

    // Close on click outside (AUTO-14)
    document.addEventListener('click', this._onClickOutside.bind(this));

    // Keyboard navigation and Escape handling (AUTO-11)
    this._input.addEventListener('keydown', this._onKeydown.bind(this));

  },

  // === Input Handling ===

  _onInput(e) {
    const query = this._input.value.trim();
    if (query.length < this.MIN_QUERY_LENGTH) {
      this._close();
      this._currentQuery = '';
      return;
    }
    if (query === this._currentQuery) return;
    this._currentQuery = query;
    this._doSearch(query);
  },

  _debounce(fn, ms) {
    return (...args) => {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => fn.apply(this, args), ms);
    };
  },

  // === API Calls ===

  async _doSearch(query) {
    // Abort any in-flight requests (AUTO-08, PERF-04)
    if (this._abortController) this._abortController.abort();
    this._abortController = new AbortController();

    this._showLoading();
    this._open();

    const concernMatch = this._matchHealthConcern(query);
    const signal = this._abortController.signal;

    // Concern fetch is best-effort: if the collection's section render fails
    // (404, server error, ratelimit, etc.) the dropdown should still show
    // predictive results. Only predictive-search failure or abort is fatal.
    const predictivePromise = this._fetchPredictiveSearch(query, signal);
    const concernPromise = concernMatch
      ? this._fetchHealthConcern(concernMatch.collection_handle, signal).catch(err => {
          if (err.name === 'AbortError') throw err;
          return null;
        })
      : Promise.resolve(null);

    try {
      const [predictiveData, concernHtml] = await Promise.all([predictivePromise, concernPromise]);

      // Count products before synonym check (SYNN-01)
      const products = (predictiveData.resources && predictiveData.resources.results && predictiveData.resources.results.products) || [];

      // Synonym follow-up if fewer than 3 products (SYNN-01)
      let synonymData = null;
      const synonymMatch = this._matchSynonym(query);
      if (synonymMatch && products.length < this.SYNONYM_MIN_PRODUCT_COUNT) {
        try {
          synonymData = await this._fetchPredictiveSearch(synonymMatch.canonical, signal);
        } catch (synErr) {
          if (synErr.name === 'AbortError') return;
          // Non-fatal -- proceed without synonym results
        }
      }

      this._renderResults(predictiveData, concernHtml, concernMatch, synonymData, synonymMatch);
    } catch (err) {
      if (err.name === 'AbortError') return; // Expected on new keystroke (D-08)
      this._close();
    }
  },

  async _fetchPredictiveSearch(query, signal) {
    const cacheKey = 'ps_' + query;
    const cached = this._cacheGet(cacheKey);
    if (cached) return cached;

    // fields = API defaults (title,product_type,variants.title,vendor) + tag.
    // Tags are NOT searched by default; adding them enables spaced-alias tags
    // (e.g. "epic defense" on EpicDefense) and the INGR-04 ingredient: tags.
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=${this.API_RESULT_LIMIT}&resources[options][fields]=title,product_type,variants.title,vendor,tag&options[unavailable_products]=last`;
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error('Predictive search fetch failed: ' + res.status);
    const data = await res.json();
    this._cacheSet(cacheKey, data, this.PREDICTIVE_CACHE_TTL);
    return data;
  },

  async _fetchHealthConcern(collectionHandle, signal) {
    // Validate handle to prevent path traversal or injection (WR-04)
    if (!/^[a-z0-9-]+$/.test(collectionHandle)) return '';

    const cacheKey = 'hc_' + collectionHandle;
    const cached = this._cacheGet(cacheKey);
    if (cached) return cached;

    const url = `/collections/${collectionHandle}?section_id=search-dropdown-products`;
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error('Health concern fetch failed: ' + res.status);
    const html = await res.text();
    this._cacheSet(cacheKey, html, this.SECTION_CACHE_TTL);
    return html;
  },

  _matchHealthConcern(query) {
    const config = window.ShopSearchConfig;
    if (!config || !config.health_concerns) return null;
    // Toggle off via theme admin: skip health concern matching entirely (BU1)
    if (config.health_concerns_enabled === false) return null;
    const q = query.toLowerCase().trim();
    const entries = Object.entries(config.health_concerns);
    for (let i = 0; i < entries.length; i++) {
      const [, concern] = entries[i];
      if (concern.keywords && concern.keywords.some(kw => q.includes(kw))) {
        return concern;
      }
    }
    return null;
  },

  _matchSynonym(query) {
    const config = window.ShopSearchConfig;
    if (!config || !config.synonyms) return null;
    const q = query.toLowerCase().trim();
    const entries = Object.entries(config.synonyms);
    for (let i = 0; i < entries.length; i++) {
      const [canonical, aliases] = entries[i];
      if (aliases.some(alias => q.includes(alias))) {
        return { canonical };
      }
    }
    return null;
  },

  // === Caching ===

  _cacheGet(key) {
    const entry = this._cache.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expires) {
      this._cache.delete(key);
      return null;
    }
    return entry.data;
  },

  _cacheSet(key, data, ttl) {
    // Evict oldest entry if cache exceeds size cap (WR-05)
    if (this._cache.size > this.CACHE_MAX_ENTRIES) {
      const firstKey = this._cache.keys().next().value;
      this._cache.delete(firstKey);
    }
    this._cache.set(key, { data, expires: Date.now() + ttl });
  },

  // === Rendering ===

  _renderResults(predictiveData, concernHtml, concernInfo, synonymData, synonymMatch) {
    this._currentConcernName = concernInfo ? concernInfo.display_name : null;
    this._dropdown.innerHTML = '';
    this._results = [];
    this._activeIndex = -1;

    let hasContent = false;

    // --- PRECOMPUTE: Build health concern nodes and concernHandles Set before any appendChild ---
    // This decouples dedupe logic from append order so products/synonyms can render first.
    let concernHandles = new Set();
    let concernContainer = null;
    let concernLink = null;

    if (concernHtml) {
      const sectionHeader = document.createElement('div');
      sectionHeader.className = 'custom-search__section-header';
      sectionHeader.textContent = 'Products for ' + concernInfo.display_name;

      concernContainer = document.createElement('div');
      concernContainer.className = 'custom-search__concern-products';
      concernContainer.setAttribute('data-testid', 'search-dropdown-health-concern');
      concernContainer.innerHTML = concernHtml;

      // Collect handles for dedup (AUTO-07, D-06) \u2014 before any appends so order is irrelevant
      concernContainer.querySelectorAll('[data-product-handle]').forEach(el => {
        concernHandles.add(el.dataset.productHandle);
      });

      // Patch ARIA + test selectors on server-rendered concern links (AUTO-12).
      // data-testid matches the predictive-search items so tests can locate
      // products uniformly regardless of which code path rendered them.
      concernContainer.querySelectorAll('.custom-search__result-item').forEach((item, i) => {
        item.setAttribute('role', 'option');
        item.setAttribute('data-testid', 'search-dropdown-result');
        if (!item.id) item.id = 'cs-concern-' + i;
      });

      concernLink = document.createElement('a');
      concernLink.href = '/collections/' + concernInfo.collection_handle;
      concernLink.className = 'custom-search__concern-link';
      concernLink.setAttribute('data-testid', 'search-dropdown-view-all');
      concernLink.innerHTML = 'Shop ' + this._escapeHtml(concernInfo.display_name) + ' &rarr;';

      concernContainer.prepend(sectionHeader);
      // Not appended yet \u2014 will be appended in order below (260428-dte)
    }

    // --- Product results from Predictive Search ---
    let products = (predictiveData.resources && predictiveData.resources.results && predictiveData.resources.results.products) || [];

    // Deduplicate products already in health concern section (AUTO-07)
    products = this._deduplicateProducts(products, concernHandles);

    // Sort sold-out products to bottom (AUTO-18)
    products = this._sortSoldOutLast(products);

    // Sort Backorder-tagged products to bottom (FX2-01)
    products = this._sortBackorderLast(products);

    // --- Collection results from Predictive Search ---
    let collections = (predictiveData.resources && predictiveData.resources.results && predictiveData.resources.results.collections) || [];

    // Filter out the health concern collection from predictive results (AUTO-06)
    if (concernInfo) {
      collections = collections.filter(c => c.handle !== concernInfo.collection_handle);
    }

    // --- Synonym follow-up results (SYNN-01, SYNN-02) ---
    // Build existingHandles from in-memory sets (concernHandles + deduped product handles)
    // rather than querying the DOM \u2014 avoids order-dependency now that concern renders after products.
    let freshSynonymProducts = [];
    let synonymDivider = null;
    let synonymSection = null;

    if (synonymData && synonymMatch) {
      const synonymProducts = (synonymData.resources && synonymData.resources.results && synonymData.resources.results.products) || [];
      const existingHandles = new Set([
        ...concernHandles,
        ...products.map(function(p) { return p.handle; })
      ]);
      freshSynonymProducts = synonymProducts.filter(function(p) { return !existingHandles.has(p.handle); });

      // Sort Backorder-tagged synonym products to bottom (FX2-01)
      freshSynonymProducts = this._sortBackorderLast(freshSynonymProducts);

      if (freshSynonymProducts.length > 0) {
        synonymDivider = document.createElement('div');
        synonymDivider.className = 'custom-search__synonym-divider';
        synonymDivider.textContent = 'Also matching: ' + this._escapeHtml(synonymMatch.canonical);

        synonymSection = document.createElement('div');
        freshSynonymProducts.forEach(function(product) {
          synonymSection.appendChild(this._renderProductItem(product));
        }.bind(this));
      }
    }

    // Section order (260428-dte): Products first, then Synonym extras, then Health concern, then Collections.
    // Direct matches appear above curated collections.

    // a) Products block
    if (products.length > 0) {
      const productsHeader = document.createElement('div');
      productsHeader.className = 'custom-search__section-header';
      productsHeader.textContent = 'Products';

      const productsSection = document.createElement('div');
      products.forEach(product => {
        productsSection.appendChild(this._renderProductItem(product));
      });

      this._dropdown.appendChild(productsHeader);
      this._dropdown.appendChild(productsSection);
      hasContent = true;
    }

    // b) Synonym block (adjacent to Products \u2014 synonym results are extra product matches)
    if (synonymDivider && synonymSection) {
      this._dropdown.appendChild(synonymDivider);
      this._dropdown.appendChild(synonymSection);
      hasContent = true;
    }

    // c) Health concern block (AUTO-04, AUTO-05)
    if (concernContainer) {
      this._dropdown.appendChild(concernContainer);
      this._dropdown.appendChild(concernLink);
      hasContent = true;
    }

    // d) Collections block
    if (collections.length > 0) {
      const collectionsHeader = document.createElement('div');
      collectionsHeader.className = 'custom-search__section-header';
      collectionsHeader.textContent = 'Collections';

      const collectionsSection = document.createElement('div');
      collections.forEach(collection => {
        collectionsSection.appendChild(this._renderCollectionItem(collection));
      });

      this._dropdown.appendChild(collectionsHeader);
      this._dropdown.appendChild(collectionsSection);
      hasContent = true;
    }

    // --- No results message ---
    if (!hasContent) {
      this._showNoResults(this._currentQuery);
    }

    // --- View all results link (AUTO-15) ---
    const viewAll = document.createElement('a');
    viewAll.href = '/search?q=' + encodeURIComponent(this._currentQuery) + '&type=product';
    viewAll.className = 'custom-search__view-all';
    viewAll.textContent = 'View all results \u2192';
    this._dropdown.appendChild(viewAll);

    // Collect all focusable result links
    this._results = Array.from(this._dropdown.querySelectorAll('a'));

    // Announce result count to screen readers (AUTO-13)
    const concernText = concernInfo ? ', including ' + concernInfo.display_name + ' products' : '';
    this._liveRegion.textContent = this._results.length + ' search results' + concernText;

    this._open();

    this._attachResultClickListeners();
  },

  _renderProductItem(product) {
    const item = document.createElement('a');
    item.href = product.url;
    item.className = 'custom-search__result-item';
    item.setAttribute('role', 'option');
    item.setAttribute('data-product-handle', product.handle);
    item.setAttribute('data-testid', 'search-dropdown-result');
    item.id = 'cs-result-' + product.id;

    // Build image src — resize to 96px for retina 48px display
    const imgSrc = product.image
      ? product.image.replace(/(\.\w+)(\?|$)/, '_96x96$1$2')
      : '';

    // Price display with sale/compare-at
    const priceHtml = product.compare_at_price && parseFloat(product.compare_at_price) > parseFloat(product.price)
      ? '<span class="custom-search__result-price--sale">' + this._formatMoney(product.price) + '</span>'
        + '<span class="custom-search__result-price--compare">' + this._formatMoney(product.compare_at_price) + '</span>'
      : this._formatMoney(product.price);

    const imgHtml = imgSrc
      ? '<img src="' + imgSrc + '" alt="' + this._escapeHtml(product.title) + '" class="custom-search__result-thumb" width="48" height="48" loading="lazy">'
      : '<div class="custom-search__result-thumb custom-search__result-thumb--placeholder"></div>';

    item.innerHTML = '<div class="custom-search__result-image">'
      + imgHtml
      + '</div>'
      + '<div class="custom-search__result-info">'
      + '<span class="custom-search__result-title">' + this._escapeHtml(product.title) + '</span>'
      + '<span class="custom-search__result-price">' + priceHtml + '</span>'
      + '</div>';

    return item;
  },

  _renderCollectionItem(collection) {
    const item = document.createElement('a');
    item.href = collection.url;
    item.className = 'custom-search__result-item';
    item.setAttribute('role', 'option');
    item.id = 'cs-collection-' + collection.id;
    item.innerHTML = '<div class="custom-search__result-info">'
      + '<span class="custom-search__result-title">' + this._escapeHtml(collection.title) + '</span>'
      + '</div>';
    return item;
  },

  _deduplicateProducts(products, concernHandles) {
    if (!concernHandles || concernHandles.size === 0) return products;
    return products.filter(p => !concernHandles.has(p.handle));
  },

  _sortSoldOutLast(products) {
    const available = products.filter(p => p.available);
    const unavailable = products.filter(p => !p.available);
    return [...available, ...unavailable];
  },

  _sortBackorderLast(products) {
    // FX2-01: Demote products tagged 'Backorder' (capital B per 260512-flx) to the end.
    // Mirrors _sortSoldOutLast shape. Stable order within each group is preserved by Array.filter.
    // Runs AFTER _sortSoldOutLast in the render path so in-stock non-backorder sits at the top.
    const inStock = products.filter(p => !(p.tags && p.tags.includes('Backorder')));
    const backorder = products.filter(p => p.tags && p.tags.includes('Backorder'));
    return [...inStock, ...backorder];
  },

  // === Utilities ===

  _formatMoney(price) {
    const moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const amount = parseFloat(price) || 0;
    const amountFixed = amount.toFixed(2);
    const amountNoDecimals = Math.round(amount).toString();
    const amountWithComma = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return moneyFormat
      .replace('{{amount}}', amountFixed)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount_with_comma_separator}}', amountWithComma);
  },

  _escapeHtml(str) {
    // XSS prevention — escape all user/API text before innerHTML insertion (T-02-04)
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  },

  // === Keyboard Navigation (AUTO-11) ===

  _onKeydown(e) {
    // Clear query on Escape even if dropdown is closed
    if (e.key === 'Escape') {
      e.preventDefault();
      this._close();
      this._input.focus();
      this._currentQuery = '';
      return;
    }

    if (!this._isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this._moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._moveActive(-1);
        break;
      case 'Enter':
        if (this._activeIndex >= 0 && this._results[this._activeIndex]) {
          e.preventDefault();
          window.location.href = this._results[this._activeIndex].href;
        }
        // If no active item, let form submit naturally (AUTO-15)
        break;
      case 'Tab':
        this._close();
        break;
    }
  },

  _moveActive(direction) {
    // Remove current highlight
    if (this._activeIndex >= 0 && this._results[this._activeIndex]) {
      this._results[this._activeIndex].classList.remove('is-active');
    }

    // Calculate new index with wrapping
    if (direction === 1) {
      this._activeIndex = this._activeIndex < this._results.length - 1
        ? this._activeIndex + 1
        : 0;
    } else {
      this._activeIndex = this._activeIndex > 0
        ? this._activeIndex - 1
        : this._results.length - 1;
    }

    // Apply highlight and update ARIA (AUTO-12)
    const activeEl = this._results[this._activeIndex];
    if (activeEl) {
      activeEl.classList.add('is-active');
      activeEl.scrollIntoView({ block: 'nearest' });
      this._input.setAttribute('aria-activedescendant', activeEl.id);
    }
  },

  // === Dropdown Control ===

  _open() {
    this._dropdown.classList.add('is-open');
    this._isOpen = true;
    this._input.setAttribute('aria-expanded', 'true');
  },

  _close() {
    this._dropdown.classList.remove('is-open');
    this._isOpen = false;
    this._activeIndex = -1;
    this._input.setAttribute('aria-expanded', 'false');
    this._input.setAttribute('aria-activedescendant', '');
    // Remove is-active from all result items
    this._results.forEach(el => el.classList.remove('is-active'));
  },

  _showLoading() {
    // AUTO-17: loading spinner during API fetch
    this._dropdown.innerHTML = '<div class="custom-search__loading"><div class="custom-search__spinner"></div></div>';
  },

  // === Analytics (Phase 4) ===

  trackSearch(query, resultsCount, concernName, searchType) {
    if (typeof gtag === 'function') {
      gtag('event', 'site_search', {
        search_term: query,
        results_count: resultsCount,
        health_concern_matched: concernName || 'none',
        search_type: searchType
      });
    }
    this._sendWebhook({
      event: 'site_search',
      query: query,
      results_count: resultsCount,
      health_concern: concernName || null,
      search_type: searchType
    });
  },

  trackNoResults(query, concernName) {
    if (typeof gtag === 'function') {
      gtag('event', 'search_no_results', {
        search_term: query,
        health_concern_matched: concernName || 'none'
      });
    }
    this._sendWebhook({
      event: 'search_no_results',
      query: query,
      results_count: 0,
      health_concern: concernName || null
    });
  },

  trackClick(query, productHandle, productTitle, clickPosition) {
    if (typeof gtag === 'function') {
      gtag('event', 'search_click', {
        search_term: query,
        clicked_product_handle: productHandle,
        clicked_product_title: productTitle,
        click_position: clickPosition
      });
    }
    this._sendWebhook({
      event: 'search_click',
      query: query,
      results_count: null,
      health_concern: null,
      clicked_product: productHandle,
      clicked_product_title: productTitle,
      click_position: clickPosition
    });
  },

  _sendWebhook(eventData) {
    var webhookUrl = window.ShopSearchConfig && window.ShopSearchConfig.webhook_url;
    if (!webhookUrl) return;
    var payload = JSON.stringify({
      event: eventData.event,
      query: eventData.query,
      results_count: eventData.results_count !== undefined ? eventData.results_count : 0,
      health_concern_matched: eventData.health_concern || null,
      health_concerns_enabled: !(window.ShopSearchConfig && window.ShopSearchConfig.health_concerns_enabled === false),
      clicked_product: eventData.clicked_product || null,
      clicked_product_title: eventData.clicked_product_title || null,
      click_position: eventData.click_position != null ? eventData.click_position : null,
      search_type: eventData.search_type || null,
      timestamp: new Date().toISOString(),
      page_url: window.location.href
    });
    var blob = new Blob([payload], { type: 'text/plain' });
    navigator.sendBeacon(webhookUrl, blob);
  },

  _attachResultClickListeners() {
    var items = this._dropdown.querySelectorAll('[data-product-handle]');
    var self = this;
    var query = this._currentQuery;
    items.forEach(function(item, index) {
      item.addEventListener('click', function() {
        var title = item.querySelector('.custom-search__result-title');
        self.trackClick(
          query,
          item.dataset.productHandle,
          title ? title.textContent.trim() : '',
          index + 1
        );
      });
    });
  },

  _showNoResults(query) {
    const noResults = document.createElement('div');
    noResults.className = 'custom-search__no-results';
    noResults.setAttribute('data-testid', 'search-dropdown-no-results');
    noResults.textContent = 'No results found for "' + query + '"';
    this._dropdown.appendChild(noResults);
    // Announce no results to screen readers (AUTO-13)
    this._liveRegion.textContent = 'No results found for ' + query;
    // ANLY-02: Track zero results
    this.trackNoResults(query, this._currentConcernName);
  },

  // === Event Handlers ===

  _onClickOutside(e) {
    // AUTO-14: close dropdown when clicking outside the search form
    if (this._isOpen && !this._form.contains(e.target)) {
      this._close();
    }
  },

};

document.addEventListener('DOMContentLoaded', () => {
  CustomSearch.init();

  // ANLY-01/02: Results page analytics (fires once on page load)
  if (window.location.pathname === '/search') {
    var searchSection = document.querySelector('[data-section-type="collection"]');
    if (searchSection) {
      var query = searchSection.dataset.searchQuery || '';
      var resultsCount = parseInt(searchSection.dataset.resultsCount || '0', 10);
      var healthConcern = searchSection.dataset.healthConcern || null;

      if (query) {
        if (resultsCount === 0) {
          CustomSearch.trackNoResults(query, healthConcern);
        } else {
          CustomSearch.trackSearch(query, resultsCount, healthConcern, 'results_page');
        }
      }
    }

    // ANLY-03: Results page click tracking (delegated)
    var resultsGrid = document.querySelector('.collection__dynamic-part');
    if (resultsGrid) {
      resultsGrid.addEventListener('click', function(e) {
        var card = e.target.closest('[data-product-handle]');
        if (!card) return;
        var allCards = resultsGrid.querySelectorAll('[data-product-handle]');
        var position = Array.from(allCards).indexOf(card) + 1;
        var sectionEl = document.querySelector('[data-section-type="collection"]');
        var searchQuery = sectionEl ? (sectionEl.dataset.searchQuery || '') : '';
        var title = card.querySelector('.product-item__title') || card.querySelector('[class*="title"]');
        CustomSearch.trackClick(
          searchQuery,
          card.dataset.productHandle,
          title ? title.textContent.trim() : '',
          position
        );
      });
    }
  }
});
